import "./MiniBracket.css";
import {
  SeriesAllianceState
} from "../../javaTypes";
import {ReactNode, useContext, useEffect, useMemo, useRef, useState} from "react";
import { RankingData } from "../rankingDataState";
import { t } from "../../i18n";
import {
  bracketTargets,
  isBasicAllianceDescription,
  isDoubleElimsSeriesDescription,
  isSeriesAllianceDescription
} from "../util";

export default function MiniBracket({divisionNumber = 0, highlightSeries} : {
  divisionNumber?: number,
  highlightSeries?: number
}) {
  const bracketContainer = useRef<HTMLDivElement | null>(null);
  const state = useContext(RankingData);

  const bracket = state[divisionNumber]?.bracket;
  const structure = state[divisionNumber]?.playoffStructure;
  const sortedSeries = useMemo(() => {
    return structure ? [...structure.series].sort((a, b) => {
      if(a.round != b.round) {
        return a.round - b.round;
      }
      if(isDoubleElimsSeriesDescription(a) && isDoubleElimsSeriesDescription(b) && a.bracketHalf != b.bracketHalf) {
        return a.bracketHalf == 'LOWER' ? 1 : -1;
      }
      // If both series have any winners, sort by the series numbers of those winners
      const aWinners: number[] = [isSeriesAllianceDescription(a.red) ? a.red.series : null, isSeriesAllianceDescription(a.blue) ? a.blue.series : null].filter(m => m != null) as number[]
      const bWinners: number[] = [isSeriesAllianceDescription(b.red) ? b.red.series : null, isSeriesAllianceDescription(b.blue) ? b.blue.series : null].filter(m => m != null) as number[]
      const firstWinner = Math.min(...aWinners, ...bWinners, 999)
      if(firstWinner < 999) {
        return aWinners.includes(firstWinner) ? -1 : 1
      }
      return a.series - b.series
    }) : undefined;
  }, [structure]);

  const maxLayers = Math.max(...(sortedSeries?.filter(p => bracket?.seriesData[p.series - 1]).map(p => p.round) || [0]))
  const matchesInRoundHalves = sortedSeries?.reduce((acc, p) => {
    acc[`${p.round}-${isDoubleElimsSeriesDescription(p) ? p.bracketHalf : 'CENTER'}`] ||= 0
    acc[`${p.round}-${isDoubleElimsSeriesDescription(p) ? p.bracketHalf : 'CENTER'}`]++
    return acc
  }, {} as Record<string, number>);
  const maxInRoundHalf = Math.max(...(matchesInRoundHalves ? Object.values(matchesInRoundHalves) : [1]))
  let firstMinimalRound = matchesInRoundHalves ? Math.min(...Object.entries(Object.entries(matchesInRoundHalves).reduce((acc, e) => {
    const round = e[0].split('-')[0]
    acc[round] ||= []
    acc[round].push(e[1])
    return acc
  }, {} as Record<string, number[]>)).filter((e) => e[1].length <= 2 && (!e[1][0] || e[1][0] == 1) && (!e[1][1] || e[1][1] == 1)).map(e => parseInt(e[0])), 999) : 1
  // Don't need special formatting for the first round for sufficiently small brackets
  if (maxInRoundHalf < 3 && maxLayers < 6) {
    firstMinimalRound = 0;
  }


  const columnGap = 200 / (maxLayers - 1);
  const [svgPaths, setSvgPaths] = useState<ReactNode>();

  // TODO this should be useLayoutEffect
  useEffect(() => {
    if(!bracketContainer.current || !sortedSeries) {
      return;
    }

    const scale = parseFloat(getComputedStyle(bracketContainer.current).getPropertyValue("--display-scale"));
    const baseRect = bracketContainer.current.getBoundingClientRect();

    const paths: ReactNode[] = []
    for(let i = 0; i < sortedSeries.length; i++) {
      const pairing = sortedSeries[i];
      const element = bracketContainer.current.getElementsByClassName(`series-${pairing.series}`)[0];
      if(!element) {
        continue;
      }
      const labelRect = element.getElementsByClassName("allianceGroupLabel")[0].getBoundingClientRect();

      const labelStart = { x: (labelRect.x + labelRect.width - baseRect.x) / scale, y: (labelRect.y + labelRect.height / 2 - baseRect.y) / scale };

      const winnerMatch = sortedSeries.find(p =>
          isSeriesAllianceDescription(p.red) && p.red.state == SeriesAllianceState.WINNER && p.red.series == pairing.series ||
          isSeriesAllianceDescription(p.blue) && p.blue.state == SeriesAllianceState.WINNER && p.blue.series == pairing.series)
      if(!winnerMatch) {
        continue;
      }
      const winnerContainer = bracketContainer.current.getElementsByClassName(`series-${winnerMatch.series}`)[0];
      if(!winnerContainer) {
          continue;
      }
      const winnerLabel = winnerContainer.getElementsByClassName("allianceGroupLabel")[0];
      const winnerPos = isSeriesAllianceDescription(winnerMatch.red) && winnerMatch.red.state == SeriesAllianceState.WINNER && winnerMatch.red.series == pairing.series ? 'top' : 'bottom'
      const winnerLabelRect = winnerLabel.getBoundingClientRect();
      const winnerLabelStart = { x: (winnerLabelRect.x - baseRect.x) / scale, y: (winnerLabelRect.y + winnerLabelRect.height * (winnerPos == 'top' ? 1 : 2) / 3 - baseRect.y) / scale };

      paths.push(<path key={pairing.series} d={`M ${labelStart.x} ${labelStart.y} h ${columnGap * (1 / 3)} V ${winnerLabelStart.y} H ${winnerLabelStart.x}`} stroke="white" fill="transparent" strokeWidth="4"/>)
    }
    setSvgPaths(paths)
  }, [sortedSeries, bracketContainer.current]);

  const [highlightWinnerTarget, highlightLoserTarget] = bracketTargets(structure, highlightSeries)
  const highlightSeriesDescription =  highlightSeries ? structure?.series[highlightSeries - 1] : undefined
  let highlightInfo: ReactNode = undefined
  const level = divisionNumber > 0 ? t`division` : t`event`;
  if(highlightSeriesDescription?.isFinals && highlightWinnerTarget) {
    // This must be the first finals match, so show appropriate text
    highlightInfo = <div className={"mini-bracket-sidebar"}>
      <span>
        <i className={"fa fa-trophy mr-2"}></i>
        <span className={"redLabel"}>{t`Red`}</span> {t`wins`}
        <i className={"fa fa-arrow-right"}></i><br/>
        {t`Red wins ${level}`}
      </span>
      <span>
        <i className={"fa fa-trophy mr-2"}></i>
        <span className={"blueLabel"}>{t`Blue`}</span> {t`wins`}
        <i className={"fa fa-arrow-right"}></i><br/>
        {t`Additional Match (${highlightWinnerTarget.series})`}<br/>
      </span>
    </div>
  } else if(highlightSeriesDescription?.isFinals) {

    // This must be the second finals match, so show appropriate text
    highlightInfo = <div className={"mini-bracket-sidebar"}>
      <span><i
          className={"fa fa-trophy"}></i> { t`Winner\nis ${level} winner`}</span>
      <span><i
          className={"fa fa-arrow-down"}></i> {t`Loser\nis ${level} finalist`}</span>
    </div>
  } else if (highlightSeries) {
    highlightInfo = <div className={"mini-bracket-sidebar"}>
      <span><i
          className={"fa fa-trophy"}></i> {highlightWinnerTarget ? t`Winner\nadvances to\n${highlightWinnerTarget.name}` : t`Winner\nis eliminated`}</span>
      <span><i
          className={"fa fa-arrow-down"}></i> {highlightLoserTarget ? t`Loser\nadvances to\n${highlightLoserTarget.name}` : t`Loser\nis eliminated`}</span>
    </div>
  }

  const cols = `repeat(${firstMinimalRound - 1}, minmax(0, 3fr)) repeat(${maxLayers - firstMinimalRound + 1}, minmax(0, 2fr))`
  const isDoubleElims = sortedSeries?.some(isDoubleElimsSeriesDescription)
  return <div className={"mini-bracket-parent"}>
    {highlightInfo}
    <div className={`mini-bracket`} style={{
      gridTemplateColumns: cols,
      gridTemplateRows: `repeat(${maxInRoundHalf}, minmax(0, 2fr)) repeat(${maxInRoundHalf}, minmax(0, 1fr))`,
      columnGap: columnGap + "px"
    }} ref={bracketContainer}>
      {sortedSeries?.map((p, index) => {
        const sectionElements = sortedSeries.filter(r => r.round == p.round && (!isDoubleElimsSeriesDescription(p) || !isDoubleElimsSeriesDescription(r) || p.bracketHalf == r.bracketHalf));
        const maxHeight = sectionElements.length == 1 && sectionElements[0].isFinals && isDoubleElims ? maxInRoundHalf * 2 : maxInRoundHalf
        const height = maxHeight / sectionElements.length;
        const slotInRound = sectionElements.indexOf(p)
        const halfOfRound = (isDoubleElimsSeriesDescription(p) ? p.bracketHalf == 'LOWER' : false) ? maxInRoundHalf + 1: 1
        const layer = bracket?.seriesData[p.series - 1]

        if(isDoubleElimsSeriesDescription(p) && p.optional && !layer) {
          return null
        }

        return <div key={p.series}
          className={`allianceGroup series-${p.series}`}
          style={{
            gridColumnStart: p.round,
            gridColumnEnd: "span 1",
            gridRowStart: halfOfRound + (slotInRound * height),
            gridRowEnd: halfOfRound + ((slotInRound + 1) * height)
          }}>
          <div className={`allianceGroupLabel ${highlightSeries == p.series ? 'highlight' : ''}`}>
            {p.shortName}
            <br />
            {/*TODO derive this better*/}
            <span
              className={isSeriesAllianceDescription(p.red) && p.red.series  == highlightSeries ? 'highlight' : ''}>{layer?.series?.red?.seed ? `${layer.series.red.seed.match("^[0-9]+$") ? 'A' : ''}${layer.series.red.seed}` : (isSeriesAllianceDescription(p.red) ? <>{structure?.series[p.red.series - 1].shortName} <i className={`fa fa-${(p.red.state == SeriesAllianceState.WINNER ? 'trophy' : 'arrow-down')}`}></i></>: (isBasicAllianceDescription(p.red) ? `A${p.red.alliance}` : ''))}</span>
            {p.round >= firstMinimalRound ? <br/> : ' '}vs{p.round >= firstMinimalRound ? <br/> : ' '}
            <span
              className={isSeriesAllianceDescription(p.blue) && p.blue.series == highlightSeries ? 'highlight' : ''}>{layer?.series?.blue?.seed ? `${layer.series.blue.seed.match("^[0-9]+$") ? 'A' : ''}${layer?.series?.blue?.seed}` : (isSeriesAllianceDescription(p.blue) ? <>{structure?.series[p.blue.series - 1].shortName} <i className={`fa fa-${(p.blue.state == SeriesAllianceState.WINNER ? 'trophy' : 'arrow-down')}`}></i></> : (isBasicAllianceDescription(p.blue) ? `A${p.blue.alliance}` : ''))}</span>
          </div>
        </div>
      })}

      <svg className={"bracketLines"}>
        <defs>
          <filter id="shadow">
            <feDropShadow dx="0.2" dy="0.4" stdDeviation="0.2" />
          </filter>
        </defs>
        {svgPaths}
      </svg>
    </div>
  </div>
}